import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown, Sparkles, Star, Award, Users,
  Calendar, GraduationCap, Check, Info,
} from "lucide-react";
import { PayPalCheckoutButton } from "../components/PayPalCheckoutButton";
import { RealNameConsentModal } from "../components/RealNameConsentModal";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 연간 멤버십 카드 정의 — UI 전용, 가격은 서버 카탈로그에서 가져옴
const ANNUAL_TIERS = [
  {
    planId: "annual_regular",
    name: "Regular",
    label_ko: "정회원 연간 멤버십",
    icon: Star,
    color: "burgundy",
    highlight: true,
    features: [
      "일반회원 혜택 전체 포함",
      "회원프로필 자유열람",
      "쪽지/관심보내기 무제한",
      "주1회 글로벌 줌미팅 참여",
      "연애사관학교 전체 강의 무제한",
      "오프라인 모임 우선 신청 및 20% DC 적용",
    ],
  },
  {
    planId: "annual_premium",
    name: "Premium",
    label_ko: "프리미엄 연간 멤버십",
    icon: Award,
    color: "stone",
    features: [
      "정회원 혜택 전체 포함",
      "리즈와 30분간 1:1 인터뷰",
      "3:3 줌데이트 1회 무료참여",
      "1:1 매칭 3회 이상 진행",
    ],
  },
  {
    planId: "annual_vip",
    name: "VIP",
    label_ko: "VIP 연간 멤버십",
    icon: Crown,
    color: "amber",
    features: [
      "Premium 혜택 전체 포함",
      "리즈와 1시간 1:1 인터뷰",
      "3:3 줌미팅 3회 참여",
      "1:1 고급매칭 3회 이상 진행",
    ],
  },
];

const ONE_TIME_SERVICES = [];

const MATCHING_TIERS = [];

const MATCHING_FEATURES = {};

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // Fallback 가격표 — API 응답 실패 / 0 일 때 사용 (백엔드 PLAN_CATALOG 와 동일)
  const PRICE_FALLBACK = {
    annual_regular: { label: "정회원 연간 멤버십", price: 350 },
    annual_premium: { label: "프리미엄 연간 멤버십", price: 700 },
    annual_vip:     { label: "VIP 연간 멤버십", price: 1400 },
    service_liz_1on1: { label: "Liz 1:1 상담", price: 100 },
    service_relationship_coaching: { label: "관계 코칭 세션", price: 100 },
    service_profile_review: { label: "프로필 리뷰 서비스", price: 100 },
    counseling_basic:   { label: "리즈 기본 상담 (30분)", price: 50 },
    counseling_deep:    { label: "리즈 심층 상담 (60분)", price: 90 },
    counseling_package: { label: "리즈 4주 코칭 패키지", price: 300 },
    matching_elite:   { label: "엘리트 매칭 컨설팅 (6개월)",   price: 500 },
    matching_highend: { label: "하이앤드 매칭 컨설팅 (6개월)", price: 1000 },
    matching_noble:   { label: "노블 매칭 컨설팅 (1년)",       price: 2000 },
    matching_royal:   { label: "로얄 매칭 컨설팅 (1년)",       price: 5000 },
  };

  const [plans, setPlans] = useState({ static: {}, dynamic: [] });
  const [monthly, setMonthly] = useState({ currencies: [], is_legacy_user: false, can_subscribe: false, billing_currency: "USD", usd_krw_rate: 0, tier: "premium" });
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [subCurrency, setSubCurrency] = useState("USD");
  const [subAmount, setSubAmount] = useState("");
  const [showRealNameSub, setShowRealNameSub] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/paypal/plans`)
      .then((r) => r.json())
      .then((d) => setPlans({ static: d.static || {}, dynamic: d.dynamic || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));

    // 월구독 설정 — 직접 금액 입력 + 통화 선택. is_legacy 여부도 함께 조회
    fetch(`${API_URL}/api/paypal/monthly-plans`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) =>
        setMonthly({
          currencies: d.currencies || [],
          is_legacy_user: !!d.is_legacy_user,
          can_subscribe: !!d.can_subscribe_monthly,
          billing_currency: d.billing_currency || "USD",
          usd_krw_rate: d.usd_krw_rate || 0,
          tier: d.tier || "premium",
        })
      )
      .catch(() => {});
  }, [user?.id, token]);

  const handleSubscribe = async () => {
    if (!token) {
      // 미로그인 → 로그인 / 회원가입 모달 열기
      const { toast } = await import("sonner");
      toast.info("결제를 진행하려면 먼저 로그인 또는 회원가입해주세요.");
      window.dispatchEvent(
        new CustomEvent("openAuthModal", { detail: { mode: "login" } })
      );
      return;
    }
    const cur = monthly.currencies.find((c) => c.code === subCurrency);
    const amt = parseFloat(subAmount);
    const { toast } = await import("sonner");
    if (!amt || isNaN(amt) || amt <= 0) {
      toast.error("결제하실 금액을 입력해주세요.");
      return;
    }
    if (cur && amt < cur.min) {
      toast.error(`최소 ${cur.symbol}${cur.min.toLocaleString()} 이상 입력해주세요.`);
      return;
    }
    // 정회원 이상: 실명 + 노출 동의 필요
    if (!(user?.real_name || "").trim() || !user?.real_name_consent) {
      setShowRealNameSub(true);
      return;
    }
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/paypal/subscriptions/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: amt, currency: subCurrency }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "구독 생성 실패");
      if (data.approve_url) {
        window.location.href = data.approve_url;
      }
    } catch (err) {
      toast.error(err.message);
      setSubscribing(false);
    }
  };

  const priceOf = (id) => {
    const apiPrice = plans.static[id]?.price;
    if (apiPrice && apiPrice > 0) return apiPrice;
    return PRICE_FALLBACK[id]?.price || 0;
  };
  const labelOf = (id) => plans.static[id]?.label || PRICE_FALLBACK[id]?.label || id;

  const handleSuccess = (record) => {
    navigate(`/payment/success?id=${record.id}`);
  };

  // 월구독 직접 입력 — 선택 통화 정보 + KRW→USD 환산 미리보기
  const selectedCur =
    monthly.currencies.find((c) => c.code === subCurrency) ||
    { code: "USD", symbol: "$", min: 5, decimals: 2, label: "USD ($)" };
  const amtNum = parseFloat(subAmount);
  const krwUsdEquiv =
    subCurrency === "KRW" && monthly.usd_krw_rate > 0 && amtNum > 0
      ? amtNum / monthly.usd_krw_rate
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white py-16">
      <RealNameConsentModal
        open={showRealNameSub}
        onClose={() => setShowRealNameSub(false)}
        onConfirmed={() => { setShowRealNameSub(false); setTimeout(() => handleSubscribe(), 100); }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center mb-16">
          <p className="text-[11px] font-bold tracking-[0.3em] text-burgundy-500 uppercase mb-3">Pricing</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
            나에게 맞는 멤버십을 선택하세요
          </h1>
          <p className="text-stone-500 text-lg max-w-2xl mx-auto leading-relaxed">
            라이프스타일에 맞춰 자유롭게 선택하실 수 있는 연간 멤버십 플랜입니다.
          </p>
          <p className="text-[11px] text-stone-400 mt-2 max-w-md mx-auto leading-relaxed">
            가격은 USD 기준으로 표시되며, 결제 시 회원님의 현지 통화로 안내됩니다.
          </p>
          {monthly.can_subscribe && (
            <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="legacy-welcome-banner">
              <Info className="w-4 h-4" />
              <span>정회원님이시군요! 아래 월간 정기결제 옵션을 이용하실 수 있습니다.</span>
            </div>
          )}
        </div>

        {/* === 정회원 월간 정기결제 (직접 금액 입력) === */}
        {monthly.can_subscribe && monthly.currencies.length > 0 && (
          <section className="mb-24" data-testid="monthly-legacy-section">
            <div className="text-center mb-10">
              <p className="text-[10px] font-bold tracking-[0.3em] text-amber-600 uppercase mb-2">Regular Member Exclusive</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">월간 정기결제 (정회원 전용)</h2>
              <p className="text-stone-500 text-sm">
                ✓ 자동갱신 · ✓ 언제든 취소 가능 · ✓ 원하는 금액으로 직접 결제
              </p>
            </div>

            <div
              className="max-w-md mx-auto bg-gradient-to-br from-amber-50 to-white rounded-3xl border-2 border-amber-200 p-8 shadow-sm"
              data-testid="monthly-custom-card"
            >
              <div className="flex justify-center mb-3">
                <span className="px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold tracking-wider">
                  정회원 월간 정기결제
                </span>
              </div>
              <p className="text-center text-sm text-stone-500 mb-6">
                매월 원하시는 금액을 직접 입력해주세요.
              </p>

              {/* 통화 선택 */}
              <div className="flex gap-2 mb-4">
                {monthly.currencies.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setSubCurrency(c.code)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition ${
                      subCurrency === c.code
                        ? "bg-amber-500 border-amber-500 text-white"
                        : "bg-white border-stone-200 text-stone-600 hover:border-amber-300"
                    }`}
                    data-testid={`currency-${c.code}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {/* 금액 입력 */}
              <div className="relative mb-2">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 text-lg font-semibold">
                  {selectedCur.symbol}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  min={selectedCur.min}
                  step={selectedCur.decimals === 0 ? 100 : 1}
                  value={subAmount}
                  onChange={(e) => setSubAmount(e.target.value)}
                  placeholder={`${selectedCur.min.toLocaleString()} 이상`}
                  className="w-full pl-9 pr-4 py-3.5 rounded-xl border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-lg font-semibold text-stone-900"
                  data-testid="monthly-amount-input"
                />
              </div>
              <p className="text-xs text-stone-400 mb-3">
                최소 결제 금액: {selectedCur.symbol}{selectedCur.min.toLocaleString()} / 월
              </p>

              {krwUsdEquiv > 0 && (
                <p
                  className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3"
                  data-testid="krw-conversion-hint"
                >
                  ≈ 매월 USD ${krwUsdEquiv.toFixed(2)} 로 청구됩니다 (현재 환율 1 USD ≈ {monthly.usd_krw_rate.toLocaleString()}₩)
                </p>
              )}

              <button
                onClick={handleSubscribe}
                disabled={subscribing}
                className="w-full mt-2 px-4 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="monthly-subscribe-btn"
              >
                {subscribing ? "결제 진행 중…" : "월간 정기결제 시작"}
              </button>
              <p className="text-[10px] text-stone-400 mt-3 text-center leading-relaxed">
                PayPal 안전결제 · 자동갱신 · 마이페이지에서 언제든 해지 가능
                <br />KRW 선택 시 현재 환율로 USD 환산하여 매월 청구됩니다.
              </p>
            </div>
          </section>
        )}

        {/* === 연간 멤버십 === */}
        <section className="mb-24" data-testid="annual-section">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">연간 일시결제 멤버십</h2>
            <p className="text-stone-500 text-sm">
              ✓ 12개월 액세스 · ✓ 자동갱신 없음 · ✓ 만료 30일 전 알림
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 일반회원 (무료) — 가입만 하면 누구나 이용 가능 */}
            <div
              className="relative bg-white rounded-3xl border border-stone-200 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-stone-300 shadow-sm"
              data-testid="annual-card-free"
            >
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-emerald-500" />
                <span className="text-xs font-semibold tracking-wider text-stone-500 uppercase">Basic</span>
              </div>
              <h3 className="text-xl font-bold text-stone-900 mb-1">일반회원</h3>
              <p className="text-stone-400 text-xs mb-6">가입 후 바로 이용 · 평생 무료</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-bold text-stone-900">USD $0</span>
                <span className="text-stone-400 text-sm">/무료</span>
              </div>
              <ul className="space-y-2.5 mb-6 min-h-[160px]">
                {[
                  "커뮤니티 전체 게시판 열람·작성",
                  "AI 추천 친구 기능",
                  "나의 SNS 관리",
                  "공개 이벤트 정보 열람",
                  "무료 콘텐츠 일부 열람",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {user ? (
                <button
                  type="button"
                  onClick={() => navigate("/community")}
                  className="w-full px-5 py-3 rounded-xl bg-white border border-stone-300 hover:bg-stone-50 text-stone-800 font-semibold text-sm tracking-wide transition-all duration-300"
                  data-testid="free-start-btn"
                >
                  커뮤니티 둘러보기
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/?signup=1")}
                  className="w-full px-5 py-3 rounded-xl bg-white border border-stone-300 hover:bg-stone-50 text-stone-800 font-semibold text-sm tracking-wide transition-all duration-300"
                  data-testid="free-signup-btn"
                >
                  무료로 가입하기
                </button>
              )}
              <p className="text-[11px] text-stone-400 mt-2 text-center">결제 없이 즉시 이용 가능</p>
            </div>

            {ANNUAL_TIERS.map((tier) => {
              const Icon = tier.icon;
              const price = priceOf(tier.planId);
              const isHighlight = tier.highlight;
              return (
                <div
                  key={tier.planId}
                  className={`relative bg-white rounded-3xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                    isHighlight
                      ? "border-burgundy-300 ring-2 ring-burgundy-100 shadow-xl"
                      : "border-stone-200 shadow-sm hover:border-stone-300"
                  }`}
                  data-testid={`annual-card-${tier.planId}`}
                >
                  {isHighlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-burgundy-500 text-white text-xs font-semibold tracking-wider">
                      가장 인기
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-5 h-5 ${isHighlight ? "text-burgundy-500" : "text-stone-500"}`} />
                    <span className="text-xs font-semibold tracking-wider text-stone-500 uppercase">{tier.name}</span>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-1">{tier.label_ko}</h3>
                  <p className="text-stone-400 text-xs mb-6">연 1회 결제 · 12개월 무제한 이용</p>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-stone-900" data-testid={`price-${tier.planId}`}>USD ${price.toLocaleString()}</span>
                    <span className="text-stone-400 text-sm">/년</span>
                  </div>
                  <p className="text-xs text-stone-400 mb-6">연간 일시결제 · 자동갱신 없음</p>
                  <ul className="space-y-2.5 mb-6 min-h-[160px]">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isHighlight ? "text-burgundy-500" : "text-emerald-500"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <PayPalCheckoutButton
                    planId={tier.planId}
                    amount={price}
                    label={labelOf(tier.planId)}
                    requireRealName={true}
                    onSuccess={handleSuccess}
                    testIdPrefix={`pay-${tier.planId}`}
                  />
                </div>
              );
            })}
          </div>
        </section>

        {/* === 어드민이 등록한 동적 항목 (이벤트 티켓, 특별 프로그램) === */}
        {plans.dynamic && plans.dynamic.length > 0 && (
          <section className="mb-24" data-testid="dynamic-section">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2">이벤트 & 특별 프로그램</h2>
              <p className="text-stone-500 text-sm">진행 중인 특별 프로그램 및 이벤트 티켓</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {plans.dynamic.map((p) => {
                const Icon = p.type === "event_ticket" ? Calendar : GraduationCap;
                return (
                  <div
                    key={p.id}
                    className="bg-white rounded-3xl border border-stone-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                    data-testid={`dynamic-card-${p.id}`}
                  >
                    <Icon className="w-7 h-7 text-burgundy-500 mb-3" />
                    <h3 className="text-lg font-semibold text-stone-900 mb-1">{p.label}</h3>
                    {p.description && <p className="text-xs text-stone-500 mb-3">{p.description}</p>}
                    <p className="text-2xl font-bold text-stone-900 mb-1">USD ${p.price.toLocaleString()}</p>
                    <p className="text-xs text-stone-400 mb-4">일회성 결제</p>
                    <PayPalCheckoutButton
                      dynamicPlanId={p.id}
                      amount={p.price}
                      label={p.label}
                      onSuccess={handleSuccess}
                      testIdPrefix={`pay-dyn-${p.id}`}
                    />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Footnote */}
        <div className="text-center text-xs text-stone-400 max-w-3xl mx-auto leading-relaxed">
          <p>가격은 USD 기준으로 표시되며, 결제는 PayPal을 통해 회원님의 현지 통화로 안내됩니다.</p>
          <p className="mt-1 text-stone-500">
            Your bank or PayPal may convert the final payment amount based on exchange rates.
            <br />은행 또는 PayPal이 환율에 따라 최종 결제 금액을 변환할 수 있습니다.
          </p>
          <p className="mt-1">연간 멤버십은 자동갱신되지 않으며, 만료 30일 전 이메일로 안내드립니다.</p>
          <p className="mt-1">레거시 회원의 월간 정기결제 옵션은 별도 안내됩니다. 문의: support@newcoupling.com</p>
        </div>
      </div>
    </div>
  );
}
